import React, {useState} from 'react';
import {
  useDebounce,
  useSearchPricingList,
  useAddPaymentItem,
  useAddInsuranceClaimItem,
} from '$modules/frontdesk/hooks';
import FrontdeskSearchbox from '$components/FrontdeskSearchBox';
import SearchRefractionIcon from '$assets/svgs/search-refraction.svg?react';
import Loading from '$components/Loading';
import {Dialog, IconButton} from '@mui/material';
import {XCloseIcon} from '$assets/svgs';
import './style.scss';
import FrontdeskButton from '~/components/FrontdeskButton';
import {PricingListItem} from '$api/frontdesk';
import {usePricingDetail} from '$modules/frontdesk/hooks';
import {SEARCH_PAYMENT_ITEM_CONSTANTS} from './constants';
import useAuthUser from '~/hooks/useAuthUser';
import {UserRoleName} from '$modules/admin/constant';
import Input from '$components/Input';
import {InsuranceClaim} from '~/api/finance/insuranceClaims/insuranceClaimDetails';
import {IInvoiceDetails} from '~/api/finance/invoice';

interface ConsultantDetail {
  id: number;
  consultant_name: string;
  consultation_fee: string;
  followup_fee: string;
  is_consultant: true;
}

interface ProcedureDetail {
  id: number;
  description: string;
  ccsd_code: string;
  category_name: string;
  procedure_price: number;
  eye_involvement: string | null;
  is_consultant: false;
}

type PricingDetail = ConsultantDetail | ProcedureDetail;

interface SearchPaymentItemProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (item: PricingListItem) => void;
  paymentId?: number;
  insuranceClaimId?: number;
  insuranceClaim?: InsuranceClaim | IInvoiceDetails['insurance_claim'];
}

const SearchPaymentItem: React.FC<SearchPaymentItemProps> = ({
  isOpen,
  onClose,
  paymentId,
  insuranceClaimId,
  insuranceClaim,
}) => {
  const [searchText, setSearchText] = useState('');
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const debouncedSearchText = useDebounce<string>(searchText, 500);
  const {roleName} = useAuthUser()!;
  const isFinanceRole = roleName === UserRoleName.Finance;

  const {data, isLoading, isError} = useSearchPricingList(debouncedSearchText);
  const {data: selectedItemDetail, isLoading: isLoadingDetail} =
    usePricingDetail(selectedItemId) as {
      data: PricingDetail | null;
      isLoading: boolean;
    };

  const {sendAddPaymentItemRequest} = useAddPaymentItem();
  const {sendAddInsuranceClaimItemRequest} = useAddInsuranceClaimItem();

  const isSearching = searchText && !selectedItemId;
  const hasSelectedItem = Boolean(selectedItemId);

  const getInsuranceClaimValidationError = () => {
    if (!insuranceClaim) return null;

    if (!insuranceClaim.membership_no) {
      return 'Membership number is required before adding items';
    }
    if (!insuranceClaim.insurer_name) {
      return 'Insurer name is required before adding items';
    }

    if (
      'invoice_issued_at' in insuranceClaim &&
      insuranceClaim.invoice_issued_at
    ) {
      return 'Invoice has already been generated. Cannot add more items';
    }
    return null;
  };

  const validationError = getInsuranceClaimValidationError();

  const handleClose = () => {
    setSearchText('');
    setSelectedItemId(null);
    setNotes('');
    onClose();
  };

  const handleSearchChange = (text: string) => {
    setSearchText(text);
    if (!text) {
      setSelectedItemId(null);
    }
  };

  const handleClick = (item: PricingListItem) => {
    setSelectedItemId(item.id);
    setSearchText(item.description);
  };

  const handleConfirm = () => {
    if (validationError) {
      return;
    }

    if (selectedItemId) {
      if (paymentId) {
        sendAddPaymentItemRequest({
          item: {
            price_list_id: selectedItemId,
            payment_id: paymentId,
          },
        });
      } else if (insuranceClaimId) {
        sendAddInsuranceClaimItemRequest({
          item: {
            price_list_id: selectedItemId,
            insurance_claim_id: insuranceClaimId,
            notes: notes.trim() || undefined,
          },
        });
      }
      handleClose();
    }
  };

  const highlightMatch = (text: string, searchText: string) => {
    if (!searchText) return text;

    const parts = text.split(new RegExp(`(${searchText})`, 'gi'));
    return (
      <span>
        {parts.map((part, index) =>
          part.toLowerCase() === searchText.toLowerCase() ? (
            <strong key={index}>{part}</strong>
          ) : (
            part
          )
        )}
      </span>
    );
  };

  return (
    <Dialog
      open={isOpen}
      className="search-payment-item-dialog"
      maxWidth="md"
      fullWidth
    >
      <div className="dialog-close">
        <IconButton onClick={handleClose}>
          <XCloseIcon />
        </IconButton>
      </div>
      <h2 className="dialog-title">
        {SEARCH_PAYMENT_ITEM_CONSTANTS.DIALOG.TITLE}
      </h2>
      <div className="dialog-body">
        <div className="search-payment-form-container">
          <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
            <div className="search-headers">
              {SEARCH_PAYMENT_ITEM_CONSTANTS.SEARCH.HEADER}
            </div>
            <div className="search-payment-dropdown">
              <FrontdeskSearchbox
                value={searchText}
                startIcon={<SearchRefractionIcon />}
                placeholder={SEARCH_PAYMENT_ITEM_CONSTANTS.SEARCH.PLACEHOLDER}
                onChange={handleSearchChange}
              />
            </div>
          </div>

          {isSearching && (
            <div
              className="search-payment-result-wrapper"
              data-testid="search-payment-result-wrapper"
            >
              {isLoading ? (
                <Loading />
              ) : isError ? (
                <div className="no-data-found">
                  {SEARCH_PAYMENT_ITEM_CONSTANTS.RESULTS.ERROR}
                </div>
              ) : data && data.length > 0 ? (
                data.map((item: PricingListItem) => (
                  <div key={item.id} onClick={() => handleClick(item)}>
                    <div className="item-container">
                      <div>
                        {highlightMatch(item.description, searchText)}
                        {item.ccsd_code && (
                          <span className="item-ccsd">
                            ({highlightMatch(item.ccsd_code, searchText)})
                          </span>
                        )}
                      </div>
                      <span className="item-price">£{item.price}</span>
                    </div>
                    <div className="item-category">{item.category_name}</div>
                  </div>
                ))
              ) : (
                <div className="no-data-found">
                  {SEARCH_PAYMENT_ITEM_CONSTANTS.RESULTS.NO_ITEMS}
                </div>
              )}
            </div>
          )}

          {hasSelectedItem && (
            <div>
              {isLoadingDetail ? (
                <Loading />
              ) : selectedItemDetail ? (
                <div
                  className="search-payment-form-container"
                  data-testid="search-payment-form-container"
                >
                  {selectedItemDetail.is_consultant ? (
                    <>
                      <div className="detail-field-container">
                        <div className="search-headers">
                          {
                            SEARCH_PAYMENT_ITEM_CONSTANTS.DETAILS.CONSULTANT
                              .NAME
                          }
                        </div>
                        <div className="detail-field">
                          {selectedItemDetail.consultant_name}
                        </div>
                      </div>

                      <div className="detail-field-container">
                        <div className="search-headers">
                          {
                            SEARCH_PAYMENT_ITEM_CONSTANTS.DETAILS.CONSULTANT
                              .CONSULTATION_FEE
                          }
                        </div>
                        <div className="detail-field">
                          £{selectedItemDetail.consultation_fee}
                        </div>
                      </div>

                      <div className="detail-field-container">
                        <div className="search-headers">
                          {
                            SEARCH_PAYMENT_ITEM_CONSTANTS.DETAILS.CONSULTANT
                              .FOLLOWUP_FEE
                          }
                        </div>
                        <div className="detail-field">
                          £{selectedItemDetail.followup_fee}
                        </div>
                      </div>

                      {insuranceClaimId && (
                        <div className="detail-field-container notes-field-container">
                          <div className="search-headers">
                            {
                              SEARCH_PAYMENT_ITEM_CONSTANTS.DETAILS.PROCEDURE
                                .NOTES
                            }
                          </div>

                          <Input
                            name="notes"
                            placeholder={
                              SEARCH_PAYMENT_ITEM_CONSTANTS.PLACEHOLDERS.NOTES
                            }
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            multiline
                            className="notes-input"
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="detail-field-container">
                        <div className="search-headers">
                          {
                            SEARCH_PAYMENT_ITEM_CONSTANTS.DETAILS.PROCEDURE
                              .DESCRIPTION
                          }
                        </div>
                        <div className="detail-field">
                          {selectedItemDetail.description}
                        </div>
                      </div>

                      <div className="detail-field-container">
                        <div className="search-headers">
                          {
                            SEARCH_PAYMENT_ITEM_CONSTANTS.DETAILS.PROCEDURE
                              .CCSD_CODE
                          }
                        </div>
                        <div className="detail-field">
                          {selectedItemDetail.ccsd_code || '-'}
                        </div>
                      </div>

                      <div className="detail-field-container">
                        <div className="search-headers">
                          {
                            SEARCH_PAYMENT_ITEM_CONSTANTS.DETAILS.PROCEDURE
                              .CATEGORY
                          }
                        </div>
                        <div className="detail-field">
                          {selectedItemDetail.category_name}
                        </div>
                      </div>

                      {selectedItemDetail.eye_involvement && (
                        <div className="detail-field-container">
                          <div className="search-headers">
                            {
                              SEARCH_PAYMENT_ITEM_CONSTANTS.DETAILS.PROCEDURE
                                .EYE_INVOLVEMENT
                            }
                          </div>
                          <div className="detail-field">
                            {selectedItemDetail.eye_involvement}
                          </div>
                        </div>
                      )}

                      <div className="detail-field-container">
                        <div className="search-headers">
                          {
                            SEARCH_PAYMENT_ITEM_CONSTANTS.DETAILS.PROCEDURE
                              .PRICE
                          }
                        </div>
                        <div className="detail-field">
                          £{selectedItemDetail.procedure_price}
                        </div>
                      </div>

                      {insuranceClaimId && (
                        <div className="detail-field-container notes-field-container">
                          <div className="search-headers">
                            {
                              SEARCH_PAYMENT_ITEM_CONSTANTS.DETAILS.PROCEDURE
                                .NOTES
                            }
                          </div>

                          <Input
                            name="notes"
                            placeholder={
                              SEARCH_PAYMENT_ITEM_CONSTANTS.PLACEHOLDERS.NOTES
                            }
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            multiline
                            className="notes-input"
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
      <div className="dialog-footer">
        {isFinanceRole && (
          <FrontdeskButton
            variant="primary"
            title={SEARCH_PAYMENT_ITEM_CONSTANTS.BUTTONS.ADD_ITEM}
            disabled={!hasSelectedItem || !!validationError}
            onClick={handleConfirm}
          />
        )}
      </div>
    </Dialog>
  );
};

export default SearchPaymentItem;
